import { Button, Tooltip } from "antd";
import { useSelector } from "react-redux";
import UnifiedDynamicForm from "./form/UnifiedDynamicForm";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../../store/store";
import { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import ProviderCard from "./ProviderCard";

const DynamicStepRenderer = ({
   step,
   formData,
   setFormData,
   next,
   back,
   currentStepIndex,
   totalSteps,
   satellites,
   selectedSatellite,
   allProviders,
   selectedProvider,
}: any) =>
{
   const { t } = useTranslation();

   // Get AOI data from Redux
   const allAOI = useSelector((state: RootState) => state.aoi.features || []);
   const activeFeatureId = useSelector(
      (state: RootState) => state.aoi.activeFeatureId,
   );

   // Auto-populate geometry from AOI if needed
   useEffect(() =>
   {
      if (
         step?.ui?.options?.["geometry"] &&
         activeFeatureId
         // && formData["geometry"] === undefined
      )
      {
         const feature = allAOI.find((f: any) => f.id === activeFeatureId);

         if (feature?.geometry)
         {
            setFormData((prev: any) => ({
               ...prev,
               geometry: feature.geometry,
            }));
         }
      }
   }, [step, activeFeatureId, allAOI, formData, setFormData]);

   // Validation: returns { isValid: boolean, errors: string[] }
   const getValidationErrors = () =>
   {
      const errors: string[] = [];
      const requiredFields = step.schema?.required || [];
      const uiOptions = step.ui?.options || {};

      // Check all required fields from schema
      for (const fieldKey of requiredFields)
      {
         const value = formData[fieldKey];
         const fieldConfig = uiOptions[fieldKey];
         const fieldLabel = fieldConfig?.title || fieldKey;

         // Empty check
         if (
            value === undefined ||
            value === null ||
            value === "" ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === "object" && Object.keys(value).length === 0)
         )
         {
            errors.push(`${t(fieldLabel)} ${t("is required")}`);
            continue;
         }

         // Widget-specific required
         if (fieldConfig?.required && value === undefined)
         {
            errors.push(`${t(fieldLabel)} ${t("is required")}`);
         }
      }

      // date_from must be before date_to (only if both are required in this step)
      if (
         requiredFields.includes("date_from") &&
         requiredFields.includes("date_to")
      )
      {
         const fromVal = formData?.date_from;
         const toVal = formData?.date_to;
         if (
            fromVal &&
            toVal &&
            typeof fromVal === "string" &&
            typeof toVal === "string"
         )
         {
            const fromDate = dayjs(fromVal);
            const toDate = dayjs(toVal);
            if (fromDate.isAfter(toDate))
            {
               errors.push(t("Date from must be before date to"));
            }
         }
      }

      return errors;
   };

   const validationErrors = useMemo(
      () => getValidationErrors(),
      [formData, step],
   );
   const isValid = validationErrors.length === 0;

   const handleNext = () =>
   {
      if (isValid)
      {
         next();
      }
   };

   if (!step) return null;

   return (
      <div className="relative flex flex-col h-full overflow-hidden">
         {/* HEADER */}
         <div className="p-6 ">
            <h1 className="text-xl font-semibold">
               {t(step?.title || step.schema?.title || step.step_id || "")}
            </h1>
            <p className="text-gray-400 text-sm mt-1">
               {t("Step")} {currentStepIndex + 2}/{totalSteps + 1}
            </p>
         </div>

         <ProviderCard
            satellites={satellites}
            selectedSatellite={selectedSatellite}
            allProviders={allProviders}
            selectedProvider={selectedProvider}
         />

         {/* CONTENT */}
         <div
            className="flex-1 overflow-auto"
            style={{ maxHeight: "calc(100% - 100px - 65px - 10px - 104px)" }}
         >
            <UnifiedDynamicForm
               step={step}
               formData={formData}
               setFormData={setFormData}
               type="add"
            />
         </div>

         {/* FOOTER */}
         <div className="flex justify-between bg-[#0f172a] px-6 py-4 border-t border-[#2a344d] absolute bottom-0 w-full">
            <Button className="btn-outline" onClick={back}>
               {t("Back")}
            </Button>

            {isValid ? (
               <Button className="btn-primary" onClick={handleNext}>
                  {t("Next")}
               </Button>
            ) : (
               <Tooltip
                  title={
                     <ul className="list-disc pl-4">
                        {validationErrors.map((err, idx) => (
                           <li key={idx}>{err}</li>
                        ))}
                     </ul>
                  }
                  placement="top"
               >
                  <Button
                     className="btn-primary"
                     // disabled
                     style={{ cursor: "not-allowed" }}
                  >
                     {t("Next")}
                  </Button>
               </Tooltip>
            )}
         </div>
      </div>
   );
};

export default DynamicStepRenderer;
